<script>
/* Imports */
import {
  mapActions,
  mapGetters,
} from 'vuex';

/* Analytics */
import {
  sendAnalyticsEvent,
  sanitizeAnalyticsString,
  urlToPID,
  getAnalyticsPriceData,
} from '../helpers/ga/analytics.js';

/* Helpers */
import {
  templateBuilder,
  convertCartConversionQueryParamsToString,
} from '../helpers/mainHelpers.js';

/* Components */
import ProductCard from '../components/ProductCard.vue';

/* Composables */
import { useGenericAnalyticsDataHelper } from '../composables/useAnalyticsData.js';

export default {
  name: 'Interstitial',
  components: {
    ProductCard,
  },
  props: {
    pvName: {
      type: String,
      default: '',
    },
    imageSrc: {
      type: String,
      default: '',
    },
    altText: {
      type: String,
      default: '',
    },
  },
  setup() {
    /* Initialize Composables */
    const { genericAnalyticsData } = useGenericAnalyticsDataHelper();
    return { genericAnalyticsData };
  },
  data() {
    return {
      className: 'app__product__interstitial',
      continueShoppingBtnTestIds: {
        host: 'continue-shopping-btn-host',
        tag: 'continue-shopping-btn-tag',
      },
      viewCartBtnTestIds: {
        host: 'view-cart-btn-host',
        tag: 'view-cart-btn-tag',
      },
    };
  },
  computed: {
    ...mapGetters({
      partNumber: 'getPartNumber',
      productId: 'getProductId',
      seoUrl: 'getSeoUrl',
      viewCartUrl: 'getViewCartUrl',
      isVisible: 'getInterstitialIsVisible',
      interstitialAccessories: 'getInterstitialAccessories',
      addedToCart: 'getAddedToCart',
      continueShopping: 'getContinueShopping',
      viewCart: 'getViewCart',
      shopForAccessories: 'getShopForAccessories',
      translations: 'getTranslations',
      currencyCode: 'getCurrencyCode',
      customerGroups: 'getCustomerGroups',
    }),
    productAddedClass() {
      return `${this.className}__added`;
    },
    containerClass() {
      return `${this.className}__container`;
    },
    productImageClass() {
      return `${this.className}__image`;
    },
    productDetailsClass() {
      return `${this.className}__details`;
    },
    productDetailsTitleClass() {
      return `${this.productDetailsClass}__title`;
    },
    productDetailsNameClass() {
      return `${this.productDetailsClass}__name`;
    },
    productBtnsClass() {
      return `${this.className}__buttons`;
    },
    continueShoppingClass() {
      return `${this.className}__continue_shopping`;
    },
    viewCartClass() {
      return `${this.className}__view_cart`;
    },
    accessoriesClass() {
      return `${this.className}__accessories`;
    },
    accessoryClass() {
      return `${this.className}__accessory`;
    },
    accessoriesContainerClass() {
      return `${this.className}__accessories__container`;
    },
    checkIconClass() {
      return `${this.className}__check-icon`;
    },
    loaderClass() {
      return `${this.className}__loader`;
    },
    activeInterstitialAccessories() {
      return this.interstitialAccessories(this.productId);
    },
    showInterstitialAccessories() {
      return !!this.activeInterstitialAccessories?.length;
    },
    computedViewCartUrl() {
      const queryParams = this.$route?.query;
      const stringifiedCartConversionQueryParams = convertCartConversionQueryParamsToString(queryParams);
      const cleanedCartConversionQueryParams = stringifiedCartConversionQueryParams.slice(1);
      return cleanedCartConversionQueryParams ? `${this.viewCartUrl}?${cleanedCartConversionQueryParams}` : this.viewCartUrl;
    },
    computedShopForAccessories() {
      return this.translations?.ITFE_PRODUCT_PAGES_SHOP_FOR_ACCESSORIES || this.shopForAccessories;
    },
  },
  watch: {
    showInterstitialAccessories: {
      handler(newValue) {
        if (newValue) this.sendViewItemListInterstitialAnalyticsEvent();
      },
      immediate: true,
    },
  },
  mounted() {
    // Scroll the window to top to make sure user sees that the product was added to cart
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  },
  methods: {
    ...mapActions(['setInterstitialIsVisible']),
    getSeoUrl(seoUrl) {
      return templateBuilder(this.seoUrl, { seoUrl });
    },
    handleContinueShopping(e) {
      e.preventDefault();
      this.setInterstitialIsVisible(false);
      this.handleButtonClick(e);
    },
    handleViewCart(e) {
      this.handleButtonClick(e);
    },
    handleButtonClick(e) {
      sendAnalyticsEvent({
        tealium_event: 'product_page_interstitial_click',
        link_type: 'button',
        product_name: sanitizeAnalyticsString(this.pvName),
        product_sku: this.partNumber,
        event_category: 'Product Page >> Interstitial',
        event_action: 'click',
        event_label: sanitizeAnalyticsString(e.target?.innerText),
        product_price: '',
      });
    },
    handleInterstitialProductCardClick(product) {
      this.sendSelectItemInterstitialAnalyticsEvent(product);
    },
    sendSelectItemInterstitialAnalyticsEvent(config) {
      const analyticsData = {
        ...this.genericAnalyticsData(
          'Interstitial',
          `${window?.utag_data?.product_name[0]} - Interstitial`,
        ),
        index: [config.productIndex + 1],
        product_id: [urlToPID(config?.product?.buyUrl) || 'NA'],
        product_brand: [window?.utag_data?.product_brand[0] || 'NA'],
        product_category: [window?.utag_data?.product_category[0] || 'NA'],
        product_variant: [sanitizeAnalyticsString(config?.product?.displayName) || 'NA'],
        product_name: [sanitizeAnalyticsString(config?.product?.displayName) || 'NA'],
        product_sku: [config?.product?.partNumber || 'NA'],
        product_url: [config?.product?.buyUrl || 'NA'],
        product_price: [getAnalyticsPriceData(config?.product)?.price],
        product_original_price: [getAnalyticsPriceData(config?.product)?.listPrice],
        is_sale: [getAnalyticsPriceData(config?.product)?.isSale],
        price_shown: [getAnalyticsPriceData(config?.product)?.priceShown],
        tealium_event: 'select_item',
      };

      sendAnalyticsEvent(analyticsData);
    },
    sendViewItemListInterstitialAnalyticsEvent() {
      const analyticsData = {
        ...this.genericAnalyticsData(
          'Interstitial',
          `${window?.utag_data?.product_name[0]} - Interstitial`,
        ),
        index: this.activeInterstitialAccessories?.map((_, index) => (index + 1).toString()),
        product_id: this.activeInterstitialAccessories?.map((product) => urlToPID(product?.buyUrl) || 'NA'),
        product_brand: this.activeInterstitialAccessories?.map(() => window?.utag_data?.product_brand[0] || 'NA'),
        product_category: this.activeInterstitialAccessories?.map(() => window?.utag_data?.product_category[0] || 'NA'),
        product_name: this.activeInterstitialAccessories?.map((product) => sanitizeAnalyticsString(product?.displayName) || 'NA'),
        product_sku: this.activeInterstitialAccessories?.map((product) => product?.priceObj?.partNumber || 'NA'),
        product_url: this.activeInterstitialAccessories?.map((product) => product?.buyUrl || 'NA'),
        product_price: this.activeInterstitialAccessories?.map((product) => getAnalyticsPriceData(product)?.price.toString()),
        product_original_price: this.activeInterstitialAccessories?.map((product) => getAnalyticsPriceData(product)?.listPrice.toString()),
        is_sale: this.activeInterstitialAccessories?.map((product) => getAnalyticsPriceData(product)?.isSale),
        price_shown: this.activeInterstitialAccessories?.map((product) => getAnalyticsPriceData(product)?.priceShown),
        tealium_event: 'view_item_list',
      };

      sendAnalyticsEvent(analyticsData);
    },
    getFormattedPrice(product) {
      return product?.priceObj?.listPrice?.formattedPrice;
    },
    getFormattedSalePrice(product) {
      return product?.priceObj?.salePrice?.formattedPrice;
    },
  },
};
</script>

<template>
  <div
    :class="className"
  >
    <div :class="productAddedClass">
      <div :class="containerClass">
        <div :class="productDetailsClass">
          <img
            :class="productImageClass"
            :src="imageSrc"
            :alt="altText"
            @click="handleContinueShopping($event)"
          >
          <div>
            <g-heading>
              <h1 :class="productDetailsTitleClass">
                <span :class="checkIconClass" />
                {{ addedToCart }}
              </h1>
            </g-heading>
            <div
              :class="productDetailsNameClass"
              @click="handleContinueShopping($event)"
              v-html="pvName"
            />
          </div>
        </div>
        <div :class="productBtnsClass">
          <g-button
            :id="continueShoppingClass"
            :dataTestId.prop="continueShoppingBtnTestIds"
            href="#"
            :class="continueShoppingClass"
            size="large"
            theme="light"
            @click="handleContinueShopping($event)"
          >
            {{ continueShopping }}
          </g-button>

          <g-button
            :id="viewCartClass"
            :dataTestId.prop="viewCartBtnTestIds"
            :href="computedViewCartUrl"
            :class="viewCartClass"
            size="large"
            theme="candy-blue"
            @click="handleViewCart($event)"
          >
            {{ viewCart }}
          </g-button>
        </div>
      </div>
    </div>
    <div
      v-show="showInterstitialAccessories"
      :class="accessoriesClass"
    >
      <g-heading
        text-align="center"
        size="1"
      >
        <h2 v-html="computedShopForAccessories" />
      </g-heading>
      <div :class="accessoriesContainerClass">
        <ProductCard
          v-for="(product, index) in activeInterstitialAccessories"
          :key="product.partNumber"
          :class="accessoryClass"
          :image-src="product.imageSrc"
          :title="product.displayName"
          :link="product.skuBuyUrl"
          :price-loading="!product.priceObj"
          :price-html="getFormattedPrice(product)"
          :sale-price-html="getFormattedSalePrice(product)"
          @click="handleInterstitialProductCardClick({
            product,
            productIndex: index,
          })"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.app__product__interstitial {
  width: 100%;
  height: 100%;
  padding-top: 3em;

  @include breakpoint('sm') {
    min-height: 23em;
  }

  a:focus {
    outline: solid 2px $color-black;
  }

  &__container {
    max-width: $breakpoint-lg;
    margin: 0 auto;
    display: flex;
    flex-direction: row;
    flex: 1 1 auto;
    gap: 0 1rem;
    /* stylelint-disable-next-line */
    flex-wrap: wrap;

    @include breakpoint('md') {
      flex-wrap: nowrap;
    }
  }

  &__added {
    padding-bottom: 1em;
    border-bottom: 1px solid $color-gray;
  }

  &__image {
    width: 100px;
    height: 100px;
    margin-right: 10px;
    cursor: pointer;
    align-self: flex-start;

    @include breakpoint('sm') {
      margin-right: 20px;
    }
  }

  &__details {
    width: 100%;
    margin: 0 1em;
    display: flex;

    @include breakpoint('sm') {
      width: auto;
      margin: 0 1em;
    }

    &__title {
      @include font-primary();
      @include font-primary-weight-bold();
      font-size: $font-size-large;
      margin-bottom: $spacing-sm;
      display: flex;
    }

    &__name {
      font-size: 16px;
      margin-left: 30px;
      text-align: left;
      cursor: pointer;
    }
  }

  &__buttons {
    margin-left: auto;
    padding-top: 10px;
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    flex-shrink: 0;

    @include breakpoint('sm') {
      margin-right: 1em;
      display: block;
      width: auto;
      text-align: right;
    }

    @include breakpoint('xl') {
      margin-right: 0;
    }

    .g__button {
      width: 100%;
    }
  }

  &__continue_shopping {
    width: 100%;
    margin: 0.5em 1em 0 1em;
    text-align: center;

    @include breakpoint('sm') {
      text-align: unset;
      margin: 0 0 0.5rem 0;
      width: unset;
    }
  }

  &__view_cart {
    width: 100%;
    margin: 0.5em 1em;
    text-align: center;

    .g__button:hover {
      background: unset;
    }

    @include breakpoint('sm') {
      text-align: unset;
      margin: 0 0 0 1em;
      width: unset;
    }
  }

  &__accessory {
    padding: 0.5em 0;
    width: 100%;

    @include breakpoint('sm') {
      padding: 0.5em 1em;
      width: 50%;
      align-items: flex-start;
    }

    @include breakpoint('md') {
      padding: 0.5em 1em;
      width: 33%;
      align-items: flex-start;
    }

    @include breakpoint('lg') {
      padding: 1em 0.5em 0.5em 0.5em;
      width: 25%;
    }
  }

  &__accessories {
    margin: 2em 0 5em 0;

    &__container {
      display: flex;
      justify-content: flex-start;
      flex-direction: row;
      flex: 1 1 auto;
      /* stylelint-disable-next-line */
      flex-wrap: wrap;
      margin: 0 auto;
      max-width: $breakpoint-lg;
    }
  }

  &__check-icon {
    width: 20px;
    height: 20px;
    margin-right: 10px;
    display: inline-block;
    background: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA2MCA0OC43NSI+Cjxwb2x5Z29uIHBvaW50cz0iNDkuMjgsMCAyMSwyNy43OSAxMC41MiwxNy40OSAwLDI4LjAxIDIxLDQ4Ljc1IDYwLDEwLjI0ICIgc3R5bGU9ImZpbGw6IzAwMCIgLz4KPC9zdmc+') 0 4px no-repeat;
  }

  &__loader {
    width: 10%;
    margin: 0 auto;
  }
}
</style>
